import clsx from "clsx";
import { Radio, RadioGroup, Label } from "@headlessui/react";
import { CheckCircleIcon } from "@phosphor-icons/react";

interface RadioOption {
	id: string | number;
	value: string;
	label?: string;
	icon?: React.ReactNode;
}

interface RadioGroupFieldProps {
	id: string;
	label: string;
	value: string;
	options: RadioOption[];
	onChange: (value: string) => void;
	required?: boolean;
	error?: string;
	disabled?: boolean;
	className?: string;
	gridCols?: number;
	showIcons?: boolean;
	disabledOptions?: string[];
	navRef?: boolean;
}

export function RadioGroupField({
	id,
	label,
	value,
	options,
	onChange,
	error,
	required = false,
	disabled = false,
	className = "",
	gridCols = 2,
	showIcons = false,
	disabledOptions = [],
	navRef = false,
}: RadioGroupFieldProps) {
	const colClass = {
		1: "jF-grid-cols-1",
		2: "jF-grid-cols-2",
		3: "jF-grid-cols-2 md:jF-grid-cols-3",
		4: "jF-grid-cols-2 md:jF-grid-cols-4",
		5: "jF-grid-cols-2 md:jF-grid-cols-5",
		6: "jF-grid-cols-2 md:jF-grid-cols-6",
		7: "jF-grid-cols-2 md:jF-grid-cols-7",
		8: "jF-grid-cols-4 md:jF-grid-cols-8",
	}[gridCols];

	return (
		<div className={className}>
			<RadioGroup
				id={id}
				aria-label={label}
				value={value}
				onChange={onChange}
				disabled={disabled}
				aria-required={required}
				aria-describedby={error ? `${id}-error` : ""}
				aria-invalid={!error}
			>
				<Label className="jF-text-base jF-font-semibold jF-leading-6 jF-text-gray-900">
					{label}
				</Label>
				<div
					style={{ gridAutoRows: "1fr" }}
					className={clsx(
						navRef ? "jF-grid-cols-2" : colClass,
						`jF-grid jF-mt-2
             jF-gap-x-2 jF-gap-y-2 md:jF-gap-x-2`,
					)}
				>
					{options.map((item) => {
						const isOptionDisabled = disabledOptions.includes(item.value);
						const optionId = `${id}-option-${item.id}`;
						return (
							<Radio
								key={item.value}
								value={item.value}
								disabled={isOptionDisabled}
								id={optionId}
								aria-describedby={
									isOptionDisabled ? `${optionId}-disabled` : undefined
								}
								className={({ checked }) =>
									clsx(
										"jF-h-full jF-flex-1 jF-min-h-11",
										disabled || isOptionDisabled
											? "jF-opacity-50 jF-cursor-not-allowed"
											: "jF-cursor-pointer",
										isOptionDisabled ? "jF-bg-gray-50" : "jF-bg-white",
										checked && !error
											? "jF-border-[--jF-theme-color] jF-ring-1 jF-ring-[--jF-theme-color]"
											: "",
										error
											? "jF-border-red-600"
											: !checked
												? "jF-border-gray-300"
												: "",
										showIcons ? "jF-flex-1" : "jF-justify-center",
										"jF-relative jF-flex jF-rounded-lg jF-border jF-p-2 jF-shadow-sm focus:jF-outline-none focus-visible:jF-outline-[--jF-theme-color] md:jF-p-3",
									)
								}
							>
								{({ checked }) => (
									<>
										<span
											className={clsx(
												showIcons ? "jF-flex jF-flex-1" : "jF-flex",
											)}
										>
											<span
												className={clsx(
													navRef ? "" : "",
													showIcons ? "jF-items-start" : "",
													"jF-flex jF-flex-col jF-justify-center",
												)}
											>
												{showIcons && item.icon && (
													<div
														className={clsx(
															!checked
																? " jF-text-gray-900"
																: "jF-text-[--jF-theme-color]",
														)}
													>
														{item.icon}
													</div>
												)}
												<Label
													as="span"
													className={clsx(
														!checked ? "jF-flex jF-text-gray-900" : "",
														"jF-text-wrap jF-text-[--jF-theme-color] jF-flex jF-text-sm jF-leading-4",
													)}
												>
													{item.label || item.value}
												</Label>
											</span>
										</span>
										<CheckCircleIcon
											className={clsx(
												!checked ? "jF-invisible" : "",
												"jF-h-5 jF-w-5 jF-text-[--jF-theme-color] jF-absolute jF-bg-white -jF-right-1 -jF-top-1",
											)}
											aria-hidden="true"
											weight="fill"
										/>
										<span
											className={clsx(
												checked
													? "jF-border-[--jF-theme-color]"
													: "jF-border-transparent",

												"jF-pointer-events-none jF-absolute jF-rounded-lg",
											)}
											aria-hidden="true"
										/>
									</>
								)}
							</Radio>
						);
					})}
				</div>
			</RadioGroup>
			{error && (
				<p
					id={`${id}-error`}
					className="jF-mt-1 jF-text-xs jF-text-red-600"
					role="alert"
					aria-live="polite"
				>
					{error}
				</p>
			)}
		</div>
	);
}
